import { motion } from "framer-motion";
import { Store, Rocket, Building2, Landmark, Megaphone, Briefcase, Home, Handshake } from "lucide-react";

const fadeIn = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

const personas = [
    { icon: Store, title: "Local businesses", body: "Get your storefront on the street in front of everyone who walks past." },
    { icon: Rocket, title: "Startups & launches", body: "Land a brand moment for a new product, app, or opening week." },
    { icon: Building2, title: "Established companies", body: "Add out-of-home to a bigger marketing mix — trackable, predictable." },
    { icon: Handshake, title: "Agencies", body: "Book screens for clients without long-form insertion orders and paperwork." },
    { icon: Megaphone, title: "Event promoters", body: "Drive last-minute awareness for a gig, festival, market, or launch night." },
    { icon: Briefcase, title: "Franchisees & multi-site", body: "Coordinate campaigns city-by-city with the same brand, one dashboard." },
    { icon: Home, title: "Real estate & property", body: "Feature a listing or a new development in the postcodes that matter." },
    { icon: Landmark, title: "Not-for-profits & councils", body: "Public-interest campaigns with clear pricing and full receipts." },
];

export const WhoItsFor = ({ onBegin }) => {
    return (
        <section
            id="who-its-for"
            data-testid="landing-who-its-for"
            className="relative py-14 lg:py-20"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeIn(0)} className="max-w-3xl mb-12">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-6 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" />
                        01 / Built for
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                        You don&apos;t need to be<br />
                        <span className="text-slate-500">a household name.</span>
                    </h2>
                    <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
                        Digital billboards used to be closed-door territory — locked behind
                        agencies, long contracts, and quotes over email. We opened the
                        catalogue so anyone with a message can book a screen.
                    </p>
                </motion.div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
                    {personas.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.title}
                                {...fadeIn(0.04 * i)}
                                data-testid={`landing-persona-${i}`}
                                className="bg-[#0F1E3B] hover:bg-[#1B2E55] p-6 lg:p-8 transition-colors duration-500"
                            >
                                <Icon className="text-[#FF5A00] mb-4" size={20} strokeWidth={1.5} />
                                <div className="font-display font-black text-base sm:text-lg text-white tracking-tight leading-snug">
                                    {p.title}
                                </div>
                                <div className="mt-3 text-xs sm:text-sm text-slate-400 leading-relaxed">
                                    {p.body}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div {...fadeIn(0.35)} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <button
                        onClick={onBegin}
                        data-testid="landing-who-cta-primary"
                        className="inline-flex items-center gap-3 bg-[#FF5A00] hover:bg-[#E65200] text-white px-7 py-3.5 text-sm font-semibold uppercase tracking-widest transition-all duration-300"
                    >
                        Put Me On A Billboard
                        <span aria-hidden>→</span>
                    </button>
                    <a
                        href="#pricing"
                        data-testid="landing-who-cta-secondary"
                        className="inline-flex items-center gap-2 text-sm font-mono-tech uppercase tracking-widest text-white/70 hover:text-white transition-colors"
                    >
                        Or see the prices first →
                    </a>
                </motion.div>
            </div>
        </section>
    );
};
